import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminProvider, useAdmin } from './context/AdminContext';
import { CartProvider } from './context/CartContext';
import LoadingSpinner from './components/shared/LoadingSpinner';
import ScrollToTop from './components/shared/ScrollToTop';

// Admin Components
import AdminNavbar from './components/admin/AdminNavbar';
import AdminSidebar from './components/admin/AdminSidebar';
import AgeVerification from './components/shared/AgeVerification';

// Admin Pages
import Login from './pages/admin/Login';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLocations from './pages/admin/AdminLocations';
import AdminEquipment from './pages/admin/AdminEquipment';
import AdminTalent from './pages/admin/AdminTalent';
import AdminBookings from './pages/admin/AdminBookings';
import TestData from './pages/admin/TestData';

// Public Pages
import Home from './pages/public/Home';
import PublicLocations from './pages/public/Locations';
import PublicEquipment from './pages/public/Equipment';
import PublicTalent from './pages/public/Talent';
import Cart from './pages/public/Cart';
import Checkout from './pages/public/Checkout';
import Confirmation from './pages/public/Confirmation';
import Contact from './pages/public/Contact';
import NotFound from './pages/public/NotFound';

import './App.css';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

const AdminLayoutContent = ({ children }) => {
  const { sidebarOpen } = useAdmin();

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className={`admin-main ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <AdminNavbar />
        <div className="admin-content">
          {children}
        </div>
      </div>
    </div>
  );
};

const AdminLayout = ({ children }) => {
  return (
    <AdminProvider>
      <AdminLayoutContent>
        {children}
      </AdminLayoutContent>
    </AdminProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <ScrollToTop />
          <AgeVerification />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/locations" element={<PublicLocations />} />
            <Route path="/equipment" element={<PublicEquipment />} />
            <Route path="/talent" element={<PublicTalent />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/confirmation/:bookingId" element={<Confirmation />} />

            {/* Admin Login */}
            <Route path="/admin/login" element={<Login />} />

            {/* Protected Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bookings"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminBookings />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/locations"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminLocations />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/equipment"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminEquipment />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/talent"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <AdminTalent />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/test-data"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <TestData />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;